import { useState } from 'react';
import * as R from 'remeda';
import { useIntl } from 'react-intl';
import { v4 as uuidv4 } from 'uuid';
import { IconCircleMinus, IconCirclePlus } from '@rossum/ui/icons/tabler';
import {
  alpha,
  Button,
  IconButton,
  Stack,
  SvgIcon,
  Typography,
} from '@rossum/ui/material';
import { FilterOperator } from '../../../mql/operators';
import {
  isOperatorEmpty,
  resolveValueBasedOnBooleanOperator,
} from '../../operatorUtils';
import { FilterValues } from '../../types';
import { ActionButtons } from '../ActionButtons';
import { ADVANCED_COMPONENT_WIDTH } from '../constants';
import { getDefaultFilterRulesState } from '../FilterChip/utils';
import { AdvancedFilterComponentProps } from '../FilterComponentProps';
import { InputSelector } from './InputSelector';
import { SelectOperators } from './SelectOperators';

// Local type: add runtime-only ID for React keys and accurately match operators to handle operation changes
type FilterRuleWithLocalId = FilterValues & { id: string };

export const AdvancedFilterComponent = ({
  filterItem,
  applyFilter,
  onClose,
}: AdvancedFilterComponentProps) => {
  const intl = useIntl();
  const { column } = filterItem;

  // Add local IDs on mount
  const [filterRules, setFilterRules] = useState<FilterRuleWithLocalId[]>(() =>
    getDefaultFilterRulesState(filterItem).map(rule => ({
      ...rule,
      id: uuidv4(),
    }))
  );

  const selectedOperatorValues = filterRules.map(
    ({ operator }) => operator.value
  );

  // Can add more rules if:
  // 1. Some operators are not yet used
  // 2. OR some operators allow multiple instances
  const canAddMoreRules = column.operators.some(
    op =>
      !selectedOperatorValues.includes(op.value) ||
      !!column.allowedMultipleOperatorInstances?.includes(op.value)
  );

  const doesEmptyValueExist = filterRules.some(
    ({ value, operator }) => !value && !isOperatorEmpty(operator)
  );

  const handleOperatorChange = (
    newOperator: FilterOperator,
    filterRuleId: string
  ) => {
    const updatedContext = filterRules.map(item => {
      const shouldBeUpdated = item.id === filterRuleId;
      return {
        ...item,
        operator: shouldBeUpdated ? newOperator : item.operator,
        value:
          shouldBeUpdated && isOperatorEmpty(newOperator)
            ? resolveValueBasedOnBooleanOperator(newOperator)
            : item.value,
      };
    });

    setFilterRules(updatedContext);
  };
  const handleFilterChange = ({
    filterContext,
    id,
  }: {
    filterContext: FilterValues;
    id: string;
  }) => {
    const updatedContext = filterRules.map(item => ({
      ...item,
      value: item.id === id ? filterContext.value : item.value,
    }));
    setFilterRules(updatedContext);
  };

  const addFilterRule = () => {
    // Find first operator that can be added:
    // 1. Prioritize unused operators
    // 2. Fall back to operators that allow multiple instances
    const operatorCandidate = column.operators.find(
      op =>
        !selectedOperatorValues.includes(op.value) ||
        column.allowedMultipleOperatorInstances?.includes(op.value)
    );

    if (operatorCandidate) {
      setFilterRules(prev =>
        prev.concat({
          id: uuidv4(),
          operator: operatorCandidate,
        })
      );
    }
  };

  const shouldFocusIndex = filterRules[
    filterRules.length === 2 ? 'findIndex' : 'findLastIndex'
  ](filter => !filter?.value);

  return (
    <Stack
      p={2}
      width={ADVANCED_COMPONENT_WIDTH}
      spacing={2}
      component="form"
      onSubmit={e => {
        e.preventDefault();
        e.stopPropagation();
        // Strip local IDs before submitting
        applyFilter(
          filterRules.map(filterRule => {
            const updatedValue = isOperatorEmpty(filterRule.operator)
              ? resolveValueBasedOnBooleanOperator(filterRule.operator)
              : filterRule.value;
            return R.omit({ ...filterRule, value: updatedValue }, ['id']);
          })
        );
        onClose();
      }}
    >
      {column.headerName && (
        <Typography variant="body2" sx={{ color: 'text.secondary' }}>
          {column.headerName}
        </Typography>
      )}
      <Stack spacing={2}>
        {filterRules.map((filterContext, index) => {
          return (
            <Stack
              key={filterContext.id}
              direction="row"
              spacing={1}
              alignSelf="flex-end"
            >
              {index > 0 ? (
                <Typography variant="body2" sx={{ alignSelf: 'center' }}>
                  {intl.formatMessage({
                    id: 'containers.filtering.filterRuleSeparator.and',
                  })}
                </Typography>
              ) : null}
              <SelectOperators
                operators={column.operators}
                selectedOperators={selectedOperatorValues}
                operator={filterContext.operator}
                onOperatorChange={operator =>
                  handleOperatorChange(operator, filterContext.id)
                }
                headerName={column.headerName || ''}
                allowedMultipleInstances={
                  column.allowedMultipleOperatorInstances
                }
              />
              <InputSelector
                handleFilterChange={newValues =>
                  handleFilterChange({
                    filterContext: newValues,
                    id: filterContext.id,
                  })
                }
                column={column}
                filterContext={filterContext}
                autoFocus={shouldFocusIndex === index}
              />
              <IconButton
                color="secondary"
                sx={{
                  alignSelf: 'center',
                  color: 'text.disabled',
                  '&:hover': {
                    backgroundColor: theme =>
                      alpha(theme.palette.text.disabled, 0.08),
                  },
                }}
                disabled={filterRules.length === 1}
                onClick={() =>
                  setFilterRules(prev =>
                    prev.filter(
                      prevContext => prevContext.id !== filterContext.id
                    )
                  )
                }
              >
                <SvgIcon fontSize="medium">
                  <IconCircleMinus />
                </SvgIcon>
              </IconButton>
            </Stack>
          );
        })}
      </Stack>
      <ActionButtons
        onCancel={onClose}
        isSubmitDisabled={doesEmptyValueExist}
        secondaryButton={
          <Button
            variant="text"
            color="secondary"
            startIcon={
              <SvgIcon fontSize="medium">
                <IconCirclePlus />
              </SvgIcon>
            }
            sx={{ textWrap: 'nowrap' }}
            onClick={addFilterRule}
            disabled={!canAddMoreRules}
            data-cy="all-documents-advanced-filter-add-rule-button"
          >
            {intl.formatMessage({
              id: 'containers.filtering.buttons.addFilterRule',
            })}
          </Button>
        }
        dataCyKey={`advanced-${column.field}`}
      />
    </Stack>
  );
};
